import type { AlgoliaJobAdsRequest } from "@/modules/jobAds/client/utils/computeAlgoliaJobAdsRequest"

import { NIL as NIL_UUID } from "uuid"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { useJobAdsSearchParams } from "@/modules/jobAds/client/hooks/useJobAdsSearchParams"
import { computeAlgoliaJobAdsRequest } from "@/modules/jobAds/client/utils/computeAlgoliaJobAdsRequest"
import { getAlgoliaIndexName } from "@/modules/jobAds/client/utils/getAlgoliaIndexName"
import { getJobAdsSearchParamsFromURLSearchParams } from "@/modules/jobAds/client/utils/getJobAdsSearchParamsFromURLSearchParams"
import { useAlgoliaIndexNameFromUnleash } from "@/modules/search/useAlgoliaIndexNameFromUnleash"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"
import { useCurrentUserSearchConfigFetch } from "@/modules/user/client/hooks/useCurrentUserSearchConfigFetch"
import { useCurrentUserSearchCriteriaFetch } from "@/modules/user/client/hooks/useCurrentUserSearchCriteriaFetch"

export const useComputeAlgoliaJobAdsRequest = (): AlgoliaJobAdsRequest => {
  const { dataCareerCenter: careerCenter } = useCareerCenterFetch()

  const { dataCurrentUser: currentUser } = useCurrentUserFetch()
  const { dataCurrentUserSearchConfig: currentSearchConfig } = useCurrentUserSearchConfigFetch()
  const { dataCurrentUserSearchCriteria: currentUserSearchCriteria } = useCurrentUserSearchCriteriaFetch()

  const searchParams = useJobAdsSearchParams()
  const {
    isAbroadOnly,
    candidacyTypes,
    companyBusinessTypes,
    companySectors,
    contractDurations,
    contractTypes,
    exclusive,
    lat,
    lng,
    locales,
    location,
    page,
    positionCategories,
    radius,
    remoteTypes,
    startDates,
    studyLevels,
    tags,
    sort,
    userQuery,
    workExperiences,
  } = getJobAdsSearchParamsFromURLSearchParams(searchParams)

  const algoliaIndexNameFromUnleash = useAlgoliaIndexNameFromUnleash()

  const indexName = getAlgoliaIndexName(algoliaIndexNameFromUnleash, sort)

  const contractTypesSearchConfig = currentSearchConfig?.data.relationships.searchFilters.data.filter(
    filter => filter.name === "contract"
  )[0]
  const studyLevelsSearchConfig = currentSearchConfig?.data.relationships.searchFilters.data.filter(
    filter => filter.name === "study_levels"
  )[0]

  const excludedStudyLevels = currentSearchConfig?.data.relationships.negativeOptions.data.studyLevels || []

  const schoolId = careerCenter?.id || NIL_UUID
  const ccPositionCategoryIds = careerCenter?.careerCenterConf?.positionCategoryIds || []

  return computeAlgoliaJobAdsRequest({
    candidacyTypes,
    ccPositionCategoryIds,
    companyBusinessTypes,
    companySectors,
    contractDurations,
    contractTypes,
    contractTypesSearchConfig,
    currentUserSearchCriteria,
    curriculumIds: currentUser?.attributes.curriculumIds || [],
    excludedStudyLevels,
    exclusive,
    facultySchoolId: careerCenter?.mainCareerCenterId,
    indexName,
    isAbroadOnly,
    isLogged: currentUser?.isLogged || false,
    lat,
    lng,
    locales,
    location,
    page,
    positionCategories,
    remoteTypes,
    schoolId,
    startDates,
    studyLevels,
    studyLevelsSearchConfig,
    tags,
    userCountry: currentUser?.attributes.country,
    userQuery,
    workExperiences,
    ...(radius && { radius }),
  })
}
